import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Waves } from "lucide-react";
import { SITE } from "@/lib/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/rooms", label: "Rooms" },
  { to: "/about", label: "About Us" },
  { to: "/facilities", label: "Facilities" },
  { to: "/gallery", label: "Gallery" },
  { to: "/location", label: "Location" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || open || pathname === "/book";

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          solid
            ? "border-b border-white/20 bg-sand/85 shadow-lg shadow-navy/5 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center border ${solid ? "border-navy/30 text-navy" : "border-white/40 text-white"}`}>
              <Waves size={20} strokeWidth={1.5} />
            </span>
            <span className="leading-tight">
              <span className={`block font-display text-lg font-semibold tracking-wide ${solid ? "text-navy" : "text-white"}`}>
                Sagar Darshan
              </span>
              <span className={`block text-[10px] font-medium uppercase tracking-[0.35em] ${solid ? "text-gold-dark" : "text-gold"}`}>
                Guest House · Somnath
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    solid
                      ? isActive ? "text-gold-dark" : "text-navy hover:text-gold-dark"
                      : isActive ? "text-gold" : "text-white/85 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" data-testid="nav-book-now-btn" className="btn-primary !px-6 !py-3">
              Book Now
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden ${solid ? "text-navy" : "text-white"}`}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-navy px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-3xl ${
                        isActive ? "text-gold" : "text-sand"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <Link to="/book" data-testid="mobile-book-now-btn" className="btn-primary mt-10 w-full">
                Book Your Stay
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
